import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { Typography, FormControl } from "@mui/material";

const CustomTextAreaField = ({
  methods,
  properties = {},
  textAreaStyle = {},
}) => {
  const { control } = methods;
  const { t } = useTranslation();

  if (!properties?.name) {
    return null;
  }

  const {
    name,
    label,
    required,
    maxLength,
    minRows = 2,
    placeholder = "",
    helperText = "",
    disabled = false,
    readOnly = false,
    resize = "vertical",
  } = properties;

  const inputId = `${name}-textarea`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          {label && (
            <label
              htmlFor={inputId}
              style={{
                fontSize: "0.75rem",
                marginBottom: 4,
                color: "var(--secondary-text)",
                display: "block",
              }}
            >
              {label} {required && "*"}
            </label>
          )}
          <TextareaAutosize
            id={inputId}
            minRows={minRows}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            style={{
              width: "100%",
              borderRadius: "12px",
              padding: 12,
              border: "none",
              boxShadow: "none",
              fontSize: "0.9rem",
              color: "var(--black)",
              backgroundColor: "var(--inputs)",
              outline: "none",
              fontFamily: "inter",
              resize,
              ...textAreaStyle,
            }}
            {...field}
          />
          {(error || helperText) && (
            <Typography
              variant="caption"
              color={error ? "error" : "textSecondary"}
              mt={0.5}
            >
              {error ? error.message : helperText}
            </Typography>
          )}
        </FormControl>
      )}
      rules={{
        required: required ? `${label} ${t("Common.Is Required")}` : false,
        maxLength: maxLength
          ? {
              value: maxLength,
              message: `${t("Common.Maximum")} ${maxLength} ${t(
                "Common.Characters"
              )}`,
            }
          : undefined,
      }}
    />
  );
};

CustomTextAreaField.propTypes = {
  methods: PropTypes.shape({
    control: PropTypes.object.isRequired,
  }).isRequired,
  properties: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    minRows: PropTypes.number,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    resize: PropTypes.oneOf(["none", "both", "horizontal", "vertical"]),
    name: PropTypes.string,
  }),
  textAreaStyle: PropTypes.object,
};

export default CustomTextAreaField;
